import { useRef, useState, type PointerEvent } from 'react';

import {
  HIGHLIGHT_OPACITY,
  HIGHLIGHT_WIDTH,
  clampPoint,
  unrotatePoint,
  type HighlightColor,
  type HighlightStroke,
  type Point,
  type Rotation,
} from '@/lib/page-edits';

import styles from './PageCanvas.module.css';

export interface PageCanvasProps {
  src: string;
  /** The rendition's pixel size; the canvas draws in that space. */
  width: number;
  height: number;
  rotate: Rotation;
  highlights: readonly HighlightStroke[];
  label: string;
  /** Given, the canvas takes a highlighter in this colour. */
  drawColor?: HighlightColor | null;
  onDraw?: (stroke: HighlightStroke) => void;
  size?: 'thumb' | 'large';
}

/** Below this, in normalized units, a pointer move adds no point. */
const MIN_STEP = 0.002;

/**
 * One page image, turned and marked the way the draft says.
 *
 * Highlights live in the unrotated image's space and turn with it, so the
 * group transform below is the one place rotation is drawn. A pointer is
 * mapped back through `unrotatePoint` for the same reason.
 */
export function PageCanvas({
  src,
  width,
  height,
  rotate,
  highlights,
  label,
  drawColor = null,
  onDraw,
  size = 'large',
}: PageCanvasProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [live, setLive] = useState<Point[] | null>(null);
  const sideways = rotate === 90 || rotate === 270;
  const viewWidth = sideways ? height : width;
  const viewHeight = sideways ? width : height;
  const drawing = Boolean(drawColor && onDraw);

  const toImagePoint = (event: PointerEvent<SVGSVGElement>): Point | null => {
    const box = svgRef.current?.getBoundingClientRect();
    if (!box || !box.width || !box.height) {
      return null;
    }
    const shown = {
      x: (event.clientX - box.left) / box.width,
      y: (event.clientY - box.top) / box.height,
    };
    return clampPoint(unrotatePoint(shown, rotate));
  };

  const onPointerDown = (event: PointerEvent<SVGSVGElement>) => {
    if (!drawing) {
      return;
    }
    const point = toImagePoint(event);
    if (!point) {
      return;
    }
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setLive([point]);
  };

  const onPointerMove = (event: PointerEvent<SVGSVGElement>) => {
    if (!live) {
      return;
    }
    const point = toImagePoint(event);
    const last = live[live.length - 1];
    if (point && Math.hypot(point.x - last.x, point.y - last.y) >= MIN_STEP) {
      setLive([...live, point]);
    }
  };

  const onPointerUp = () => {
    if (!live || !drawColor || !onDraw) {
      return;
    }
    // A click with no drag is still a mark: a two-point stroke draws a dot.
    onDraw({ color: drawColor, points: live.length === 1 ? [live[0], live[0]] : live });
    setLive(null);
  };

  return (
    <svg
      aria-label={label}
      className={`${styles.canvas} ${styles[size]} ${drawing ? styles.drawing : ''}`}
      onPointerCancel={() => setLive(null)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      ref={svgRef}
      role="img"
      viewBox={`0 0 ${viewWidth} ${viewHeight}`}
    >
      <g transform={groupTransform(rotate, width, height)}>
        <image height={height} href={src} preserveAspectRatio="none" width={width} />
        {highlights.map((stroke, index) => (
          <Stroke
            color={stroke.color}
            height={height}
            key={index}
            points={stroke.points}
            width={width}
          />
        ))}
        {live && drawColor ? (
          <Stroke color={drawColor} height={height} points={live} width={width} />
        ) : null}
      </g>
    </svg>
  );
}

function Stroke({
  points,
  color,
  width,
  height,
}: {
  points: readonly Point[];
  color: string;
  width: number;
  height: number;
}) {
  return (
    <polyline
      data-highlight={color}
      fill="none"
      points={points.map((point) => `${point.x * width},${point.y * height}`).join(' ')}
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeOpacity={HIGHLIGHT_OPACITY}
      strokeWidth={HIGHLIGHT_WIDTH * height}
    />
  );
}

function groupTransform(rotate: Rotation, width: number, height: number): string | undefined {
  switch (rotate) {
    case 90:
      return `translate(${height} 0) rotate(90)`;
    case 180:
      return `translate(${width} ${height}) rotate(180)`;
    case 270:
      return `translate(0 ${width}) rotate(270)`;
    default:
      return undefined;
  }
}
